import React, { useEffect, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useClassName } from 'common/hooks';
import { track } from 'utils/analytics';
import './translator.less';

const LANGUAGES = [
  { code: 'en', native: 'English', analytics: 'English', flag: 'US' },
  { code: 'es', native: 'Español', analytics: 'Spanish', flag: 'MX' },
  { code: 'fr', native: 'Français', analytics: 'French', flag: 'FR' },
  { code: 'de', native: 'Deutsch', analytics: 'German', flag: 'DE' },
  { code: 'it', native: 'Italiano', analytics: 'Italian', flag: 'IT' },
  { code: 'pt', native: 'Português', analytics: 'Portuguese', flag: 'BR' },
  { code: 'ar', native: 'العربية', analytics: 'Arabic', flag: 'SA' },
  { code: 'iw', native: 'עברית', analytics: 'Hebrew', flag: 'IL' },
  { code: 'ja', native: '日本語', analytics: 'Japanese', flag: 'JP' },
  { code: 'zh-CN', native: '简体中文', analytics: 'Chinese', flag: 'CN' },
  { code: 'zh-TW', native: '繁體中文', analytics: 'Chinese', flag: 'TW' },
];

const DEFAULT_LANGUAGE = LANGUAGES[0];
const INCLUDED_LANGUAGES = LANGUAGES.map((language) => language.code).join();
const TRANSLATOR_SCRIPT_ID = 'ad-translator-script';

const clearGoogleTranslateCookie = () => {
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const { hostname } = window.location;
  const rootDomain = hostname.split('.').slice(-2).join('.');
  ['', hostname, `.${hostname}`, rootDomain, `.${rootDomain}`].forEach(
    (domain) => {
      const domainPart = domain ? `;domain=${domain}` : '';
      document.cookie = `googtrans=;${expired};path=/${domainPart}`;
    }
  );
};

const setGoogleTranslateCookie = (code) => {
  clearGoogleTranslateCookie();
  if (code === DEFAULT_LANGUAGE.code) return;
  const value = `/en/${code}`;
  const { hostname } = window.location;
  const rootDomain = hostname.split('.').slice(-2).join('.');
  document.cookie = `googtrans=${value};path=/`;
  document.cookie = `googtrans=${value};path=/;domain=.${rootDomain}`;
};

const getActiveLanguageCode = () => {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!match) return DEFAULT_LANGUAGE.code;
  const target = decodeURIComponent(match[1]).split('/')[2];
  return LANGUAGES.some((language) => language.code === target)
    ? target
    : DEFAULT_LANGUAGE.code;
};

const CaretIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false">
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Translator = () => {
  const className = useClassName('Translator');
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeCode, setActiveCode] = useState(DEFAULT_LANGUAGE.code);

  useEffect(() => {
    setActiveCode(getActiveLanguageCode());

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          includedLanguages: INCLUDED_LANGUAGES,
          autoDisplay: false,
        },
        'google-translate'
      );
    };

    if (!document.querySelector(`#${TRANSLATOR_SCRIPT_ID}`)) {
      const script = document.createElement('script');
      script.id = TRANSLATOR_SCRIPT_ID;
      script.src =
        '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      document.body.appendChild(script);
    } else if (window.google?.translate) {
      window.googleTranslateElementInit();
    }
  }, []);

  // Closes the dropdown when clicking outside of it.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  const handleSelect = (language) => {
    if (language.code === activeCode) {
      setOpen(false);
      return;
    }
    setOpen(false);
    track(`${language.analytics} language selected`);
    setGoogleTranslateCookie(language.code);
    window.setTimeout(() => window.location.reload(), 200);
  };

  const activeLanguage =
    LANGUAGES.find((language) => language.code === activeCode) ||
    DEFAULT_LANGUAGE;

  return (
    <div className={className('container')} ref={rootRef}>
      <button
        type="button"
        className={className('trigger')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        onClick={() => setOpen((value) => !value)}>
        <div className={className('flag')} aria-hidden="true">
          <ReactCountryFlag countryCode={activeLanguage.flag} svg />
        </div>
        <span className={className('label')} translate="no">
          {activeLanguage.native}
        </span>
        <CaretIcon className={className(['caret', open && 'caret-open'])} />
      </button>

      {open && (
        <ul className={className('menu')} role="listbox">
          {LANGUAGES.map((language) => {
            const isActive = language.code === activeLanguage.code;
            return (
              <li
                key={language.code}
                role="option"
                aria-selected={isActive}
                className={className(['option', isActive && 'option-active'])}
                onClick={() => handleSelect(language)}>
                <div className={className('flag')} aria-hidden="true">
                  <ReactCountryFlag countryCode={language.flag} svg />
                </div>
                <span translate="no">{language.native}</span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Hidden host for the Google Translate engine. */}
      <div id="google-translate" className={className('gadget')} />
    </div>
  );
};

export default Translator;
